// Robust weights for the shape fits, and splitting a label's points into separate parts.

/** Typical scan noise (m): the scale below which a radius or thickness can't be told from the noise. */
export const NOISE = 0.025;

/** Weighted median of |r| (entries with w > 0) times 1.4826: σ of the inliers, if they are Gaussian. */
export function sigmaMAD(r: ArrayLike<number>, w?: ArrayLike<number> | null) {
  const a: number[] = [];
  for (let i = 0; i < r.length; i++) if (!w || w[i] > 0) a.push(Math.abs(r[i]));
  if (!a.length) return 0;
  a.sort((x, y) => x - y);
  return 1.4826 * a[a.length >> 1];
}

/** Huber weights of residuals r at scale σ (k σ: where they start to fall off); 0 beyond trim σ (0: no trim). */
export function huberW(r: ArrayLike<number>, sigma: number, trim = 0, out = new Float64Array(r.length), k = 1.345) {
  const c = k * Math.max(sigma, 1e-9), cut = trim * Math.max(sigma, 1e-9);
  for (let i = 0; i < r.length; i++) {
    const a = Math.abs(r[i]);
    out[i] = trim && a > cut ? 0 : a <= c ? 1 : c / a;
  }
  return out;
}

/** Quantile q (0..1) of the values. */
export function quantile(v: ArrayLike<number>, q: number) {
  const a = Float64Array.from(v).sort();
  if (!a.length) return NaN;
  return a[Math.min(a.length - 1, Math.max(0, Math.round(q * (a.length - 1))))];
}

/**
 * Connected parts of a point set: points within reach of each other (at least; voxels of that size
 * touching) are one part. Parts smaller than minPts are dropped (-1). Parts are numbered from the
 * biggest.
 */
export function components(xyz: Float32Array, reach = 0.04, minPts = 50): { part: Int32Array; n: number; dropped: number } {
  // cells numbered as first met, in an open-addressing hash of their integer coordinates (a Map of millions is slow)
  const n = xyz.length / 3, cellOf = new Int32Array(n);
  let cap = 1 << 16, slot = new Int32Array(cap), cx = new Int32Array(cap >> 1), cy = new Int32Array(cap >> 1), cz = new Int32Array(cap >> 1), cells = 0;
  const hash = (x: number, y: number, z: number) => {   // mixed well (murmur's finish): neighbours land apart, probes stay short
    let h = Math.imul(x, 0x9e3779b1) ^ Math.imul(y, 0x85ebca77) ^ Math.imul(z, 0xc2b2ae3d);
    h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
    h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
    return (h ^ (h >>> 16)) >>> 0;
  };
  /** The cell at x, y, z (-1: none). */
  const find0 = (x: number, y: number, z: number) => {
    for (let h = hash(x, y, z) & (cap - 1); ; h = (h + 1) & (cap - 1)) {
      const c = slot[h] - 1;
      if (c < 0) return -1;
      if (cx[c] === x && cy[c] === y && cz[c] === z) return c;
    }
  };
  const put = (c: number) => { let h = hash(cx[c], cy[c], cz[c]) & (cap - 1); while (slot[h]) h = (h + 1) & (cap - 1); slot[h] = c + 1; };
  for (let i = 0; i < n; i++) {
    const x = Math.floor(xyz[3 * i] / reach), y = Math.floor(xyz[3 * i + 1] / reach), z = Math.floor(xyz[3 * i + 2] / reach);
    let c = find0(x, y, z);
    if (c < 0) {
      if (cells === cap >> 1) {   // half full: twice the size
        cap <<= 1;
        slot = new Int32Array(cap);
        const grow = (a: Int32Array) => { const o = new Int32Array(cap >> 1); o.set(a); return o; };
        cx = grow(cx); cy = grow(cy); cz = grow(cz);
        for (let d = 0; d < cells; d++) put(d);
      }
      c = cells++;
      cx[c] = x; cy[c] = y; cz[c] = z;
      put(c);
    }
    cellOf[i] = c;
  }
  const up = new Int32Array(cells).map((_, i) => i);
  const find = (a: number): number => { while (up[a] !== a) { up[a] = up[up[a]]; a = up[a]; } return a; };
  for (let c = 0; c < cells; c++) {
    const x = cx[c], y = cy[c], z = cz[c];
    for (let i = -1; i <= 1; i++) for (let j = -1; j <= 1; j++) for (let k = -1; k <= 1; k++) {
      const d = find0(x + i, y + j, z + k);
      if (d > c) { const a = find(c), b = find(d); if (a !== b) up[a] = b; }
    }
  }
  const size = new Int32Array(cells);
  for (let i = 0; i < n; i++) size[find(cellOf[i])]++;
  const order: number[] = [];
  for (let r = 0; r < cells; r++) if (size[r] >= minPts) order.push(r);
  order.sort((a, b) => size[b] - size[a] || a - b);
  const num = new Int32Array(cells).fill(-1);
  order.forEach((r, k) => { num[r] = k; });
  const part = new Int32Array(n);
  let dropped = 0;
  for (let i = 0; i < n; i++) { const k = num[find(cellOf[i])]; part[i] = k; if (k < 0) dropped++; }
  return { part, n: order.length, dropped };
}

/** The points of one part (packed triples). */
export function pick(a: Float32Array, part: Int32Array, k: number) {
  let m = 0;
  for (const p of part) if (p === k) m++;
  const out = new Float32Array(3 * m);
  for (let i = 0, j = 0; i < part.length; i++) if (part[i] === k) out.set(a.subarray(3 * i, 3 * i + 3), 3 * j++);
  return out;
}

/** Every k-th point so at most cap remain, of both arrays (packed triples, same order). */
export function thinPair(a: Float32Array, b: Float32Array, cap: number) {
  const n = a.length / 3;
  if (n <= cap) return [a, b];
  const oa = new Float32Array(3 * cap), ob = new Float32Array(3 * cap), k = n / cap;
  for (let i = 0; i < cap; i++) {
    const j = 3 * Math.floor(i * k);
    oa.set(a.subarray(j, j + 3), 3 * i);
    ob.set(b.subarray(j, j + 3), 3 * i);
  }
  return [oa, ob];
}
